import {
  Avatar,
  Button,
  Card,
  Chip,
  Input,
  Table,
} from '@heroui/react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Trash2 } from 'lucide-react'
import * as React from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { authApiClient } from '~/lib/api-client'
import { useDebounce } from 'use-debounce'
import { DeleteConfirmModal } from './-components/delete-confirm-modal'

const shopsSearchSchema = z.object({
  name: z.string().optional().catch(''),
  page: z.number().catch(1),
  pageSize: z.number().catch(20),
  ratingScoreMin: z.number().optional().catch(undefined),
  acceptsCreditCard: z.boolean().optional().catch(undefined),
  acceptsQrCode: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/admin/shops')({
  validateSearch: shopsSearchSchema,
  component: ShopsPage,
})

function ShopsPage() {
  const { name, page, pageSize, ratingScoreMin, acceptsCreditCard, acceptsQrCode } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  // Local state for debouncing input so we don't spam URL updates
  const [localSearch, setLocalSearch] = React.useState(name || '')
  const [debouncedSearch] = useDebounce(localSearch, 300)
  const [deletingShop, setDeletingShop] = React.useState<{ id: string; name: string } | null>(null)

  React.useEffect(() => {
    if (debouncedSearch !== (name || '')) {
      navigate({ search: (prev) => ({ ...prev, name: debouncedSearch, page: 1 }) })
    }
  }, [debouncedSearch, name, navigate])

  const { data: shopsData, isFetching } = useQuery({
    queryKey: ['admin', 'shops', 'cn', name, page, pageSize, ratingScoreMin, acceptsCreditCard, acceptsQrCode],
    queryFn: async () => {
      const res = await authApiClient.shops['with-translation'][':locale'].$get({
        param: { locale: 'cn' },
        query: { 
          name: name || undefined, 
          page: String(page), 
          pageSize: String(pageSize),
          ...(ratingScoreMin ? { ratingScoreMin: String(ratingScoreMin) } : {}),
          ...(acceptsCreditCard !== undefined ? { acceptsCreditCard: String(acceptsCreditCard) } : {}),
          ...(acceptsQrCode !== undefined ? { acceptsQrCode: String(acceptsQrCode) } : {}),
        } as any,
      })
      if (!res.ok) throw res
      return await res.json()
    },
    placeholderData: keepPreviousData,
  })

  const shops = shopsData?.data || []


  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authApiClient.shops[':id'].$delete({
        param: { id },
        header: { Authorization: '' }
      })
      if (!res.ok) throw new Error('Failed to delete shop')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'shops'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
      toast.success('店铺删除成功')
      setDeletingShop(null)
    },
    onError: () => {
      toast.error('删除店铺失败')
    }
  })
  

  // TanStack Table Setup
  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<any>()
    return [
    columnHelper.display({
      id: 'shopName',
      header: '店铺名称',
      cell: info => {
        const shop = info.row.original
        const name = shop.translation?.name
        return (
          <div className="flex items-center gap-3">
            <Avatar size="md" className="rounded-xl border border-divider">
              <Avatar.Image src={shop.imageUrl} alt={name || '未知店铺'} />
              <Avatar.Fallback>{name?.[0] || '?'}</Avatar.Fallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-bold truncate max-w-[200px]">{name || '未知店铺'}</span>
              <span className="text-[10px] text-muted-foreground font-mono uppercase">{shop.sourceId}</span>
            </div>
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'addressAndTel',
      header: '地址与电话',
      cell: info => {
        const shop = info.row.original
        const address = shop.translation?.address
        return (
          <div>
            <div className="text-xs font-medium text-muted-foreground max-w-[250px] truncate">{address || '地址未提供'}</div>
            <div className="text-[10px] italic">{shop.tel || '无电话'}</div>
          </div>
        )
      }
    }),
    columnHelper.accessor('ratingScore', {
      header: '评分',
      cell: info => {
        const shop = info.row.original
        return (
          <div className="flex items-center gap-1">
            <span className="text-sm font-black text-orange-500">{shop.ratingScore}</span>
            <span className="text-[10px] text-muted">({shop.reviewCount})</span>
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'payment',
      header: '支付方式',
      cell: info => {
        const shop = info.row.original
        return (
          <div className="flex gap-1">
            {shop.acceptsCreditCard && <Chip size="sm" variant="soft" color="accent">CC</Chip>}
            {shop.acceptsQrCode && <Chip size="sm" variant="soft" color="success">QR</Chip>}
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'sourceUrl',
      header: '源地址',
      cell: info => {
        const shop = info.row.original
        const url = shop.translation?.url
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline truncate max-w-[200px] inline-block"
            title={url}
          >
            {new URL(url).hostname}…
          </a>
        ) : (
          <span className="text-xs text-muted italic">无源地址</span>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">操作</div>,
      cell: info => {
        const shop = info.row.original
        return (
          <div className="flex justify-end gap-2 items-center">
            <Button variant="danger-soft" size="sm" isIconOnly aria-label={`删除 ${shop.translation?.name || '店铺'}`} onPress={() => setDeletingShop({ id: String(shop.id), name: shop.translation?.name || '该店铺' })}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        )
      }
    }),
  ]
  }, [])

  const table = useReactTable({
    data: shops,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">店铺管理</h1>
          <p className="text-muted mt-1 font-medium text-sm">仅支持干预性管理。店铺数据由 ETL 自动化同步更新。</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="搜索店铺名称..."
            className="w-full sm:w-56"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
          <select 
            className="w-full sm:w-32 px-3 py-2 text-sm bg-background border border-input rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={ratingScoreMin || ''}
            onChange={e => navigate({ search: (prev) => ({ ...prev, ratingScoreMin: e.target.value ? Number(e.target.value) : undefined, page: 1 }) })}
          >
            <option value="">全部评分</option>
            <option value="4.5">4.5及以上</option>
            <option value="4.0">4.0及以上</option>
            <option value="3.5">3.5及以上</option>
            <option value="3.0">3.0及以上</option>
          </select>
          <div className="flex items-center gap-2">
            <Button
              variant={acceptsCreditCard ? 'primary' : 'outline'}
              size="sm"
              onPress={() => navigate({ search: (prev) => ({ ...prev, acceptsCreditCard: acceptsCreditCard ? undefined : true, page: 1 }) })}
            >
              刷卡
            </Button>
            <Button
              variant={acceptsQrCode ? 'primary' : 'outline'}
              size="sm"
              onPress={() => navigate({ search: (prev) => ({ ...prev, acceptsQrCode: acceptsQrCode ? undefined : true, page: 1 }) })}
            >
              二维码
            </Button>
          </div>
        </div>
      </div>

      <Card variant="secondary" className={isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label="Shops table">
              <Table.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Column key={header.id} isRowHeader={header.column.id === 'shopName'}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.Column>
                    ))}
                  </React.Fragment>
                ))}
              </Table.Header>
              <Table.Body>
                {table.getRowModel().rows.map(row => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Table.Cell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </Card>

      {/* 分页控件 */}
      {shopsData?.pagination && shopsData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted">
            共 {shopsData.pagination.total} 条记录，第 {shopsData.pagination.currentPage} / {shopsData.pagination.totalPages} 页
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page <= 1}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page - 1 }) })}
            >
              上一页
            </Button>
            {Array.from({ length: Math.min(5, shopsData.pagination.totalPages) }, (_, i) => {
              const totalPages = shopsData.pagination.totalPages
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'primary' : 'ghost'}
                  size="sm"
                  onPress={() => navigate({ search: (prev) => ({ ...prev, page: pageNum }) })}
                >
                  {pageNum}
                </Button>
              )
            })}
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page >= shopsData.pagination.totalPages}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page + 1 }) })}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
      
      <DeleteConfirmModal
        isOpen={!!deletingShop}
        onClose={() => setDeletingShop(null)}
        entityName="店铺"
        entityLabel={deletingShop?.name}
        onConfirm={() => deletingShop && deleteMutation.mutate(deletingShop.id)}
        isPending={deleteMutation.isPending}
        warningText="此操作将永久删除该店铺及其所有翻译、分类关联、爬虫原始数据、向量数据和用户收藏记录。"
      />
    </div>
  )
}
